import React, { useEffect, useRef, useState } from 'react';
import {
  Linkedin,
  Instagram,
  Twitter,
  Link2,
  FileText,
  Globe,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Nomination, Category } from '../../lib/supabase';
import { parseProjectUrls } from '../../lib/projectUrls';
import { useAuth } from '../../hooks/useAuth';

const SOCIAL_META: Record<string, { label: string; Icon: React.ComponentType<{ size?: number }>; color: string }> = {
  linkedin: { label: 'LinkedIn', Icon: Linkedin, color: '#0a66c2' },
  instagram: { label: 'Instagram', Icon: Instagram, color: '#e1306c' },
  x: { label: 'X', Icon: Twitter, color: '#1da1f2' },
  otro: { label: 'Enlace', Icon: Link2, color: '#64748b' },
};

const parseSocialLinks = (value: string): { type: string; url: string }[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.filter((l: any) => l && typeof l.url === 'string');
  } catch {}
  return value
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((url) => ({ type: 'otro', url }));
};

const SocialLinksList: React.FC<{ value: string }> = ({ value }) => {
  const links = parseSocialLinks(value);
  if (!links.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {links.map((link, idx) => {
        const meta = SOCIAL_META[link.type] || SOCIAL_META.otro;
        const Icon = meta.Icon;
        return (
          <a
            key={idx}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 14px',
              borderRadius: 8,
              background: 'rgba(255,255,255,0.05)',
              border: `1px solid ${meta.color}55`,
              color: '#fff',
              textDecoration: 'none',
              fontSize: '0.9rem',
              maxWidth: '100%',
            }}
          >
            <span style={{ color: meta.color, display: 'inline-flex' }}>
              <Icon size={16} />
            </span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>
              {meta.label}
            </span>
          </a>
        );
      })}
    </div>
  );
};

interface NominationDetailsProps {
  nomination: Nomination;
  category?: Category;
  onClose: () => void;
  onApprove?: () => void;
  onReject?: () => void;
  showActions?: boolean;
  showClose?: boolean;
  noDetailsScroll?: boolean;
  showTitle?: boolean;
  publicView?: boolean;
}

const NominationDetails: React.FC<NominationDetailsProps> = ({
  nomination,
  category,
  onClose,
  onApprove,
  onReject,
  showActions = false,
  showClose = true,
  noDetailsScroll = false,
  showTitle = true,
  publicView = false,
}) => {
  const isPersonal = category?.type === 'personal';
  const isBenchmark = category?.type === 'benchmark';
  const { isPublic, isNovagobTeam, isJury } = useAuth();
  const showStatusMessage = !publicView && isPublic && !isNovagobTeam && !isJury;

  // ---- Stepper móvil ----------------------------------------------------
  // En móvil mostramos solo una sección a la vez con navegación Anterior /
  // Siguiente para que sea fácilmente legible (especialmente para personas
  // del jurado consultando desde el teléfono). En desktop NO se aplica:
  // la CSS en Dashboard.css solo activa el comportamiento bajo 768px.
  const detailsRef = useRef<HTMLDivElement>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [steps, setSteps] = useState<{ title: string }[]>([]);

  // En móvil los botones de Aprobar/Rechazar quedan ocultos hasta que se
  // pulse "Decidir candidatura" — evita pulsaciones accidentales mientras
  // se navega por las secciones.
  const [actionsRevealed, setActionsRevealed] = useState(false);

  // Reconstruye la lista de pasos cada vez que cambia la candidatura mostrada
  useEffect(() => {
    const el = detailsRef.current;
    if (!el) return;
    const sections = el.querySelectorAll<HTMLElement>('.detail-section');
    const list: { title: string }[] = [];
    sections.forEach((s) => {
      const t = s.querySelector('h3')?.textContent?.trim() || 'Detalle';
      list.push({ title: t });
    });
    setSteps(list);
    setStepIndex(0);
    setActionsRevealed(false);
  }, [nomination.id, category?.id, isPersonal, isBenchmark, publicView]);

  // Marca la sección activa para que la CSS móvil oculte las demás
  useEffect(() => {
    const el = detailsRef.current;
    if (!el) return;
    const sections = el.querySelectorAll<HTMLElement>('.detail-section');
    sections.forEach((s, i) => {
      s.classList.toggle('is-step-active', i === stepIndex);
    });
  }, [stepIndex, steps.length]);

  const goPrev = () => setStepIndex((i) => Math.max(0, i - 1));
  const goNext = () =>
    setStepIndex((i) => Math.min(Math.max(0, steps.length - 1), i + 1));

  const currentStepTitle = steps[stepIndex]?.title || '';
  const totalSteps = steps.length;
  const atFirst = stepIndex <= 0;
  const atLast = stepIndex >= totalSteps - 1;

  return (
    <div>
      {showTitle && (
        <div className="modal-header">
          <h2>Detalles de la Candidatura</h2>
        </div>
      )}

      {/* Barra de pasos: solo visible en móvil (CSS) */}
      {totalSteps > 1 && (
        <div className="nomination-stepper" aria-label="Navegación entre secciones">
          <button
            type="button"
            className="nomination-stepper-btn"
            onClick={goPrev}
            disabled={atFirst}
            aria-label="Sección anterior"
          >
            <ChevronLeft size={18} />
            <span>Anterior</span>
          </button>
          <div className="nomination-stepper-info">
            <div className="nomination-stepper-counter">
              {stepIndex + 1} / {totalSteps}
            </div>
            <div className="nomination-stepper-title" title={currentStepTitle}>
              {currentStepTitle}
            </div>
          </div>
          <button
            type="button"
            className="nomination-stepper-btn"
            onClick={goNext}
            disabled={atLast}
            aria-label="Sección siguiente"
          >
            <span>Siguiente</span>
            <ChevronRight size={18} />
          </button>
        </div>
      )}

      <div
        ref={detailsRef}
        className={`nomination-details${noDetailsScroll ? ' nomination-details--no-scroll' : ''}`}
      >
        {showStatusMessage && nomination.status === 'approved' && (
          <div
            style={{
              padding: '14px 16px',
              borderRadius: 8,
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.4)',
              color: '#bbf7d0',
              marginBottom: 20,
              fontSize: '0.95rem',
              lineHeight: 1.5,
            }}
          >
            <strong style={{ color: '#86efac', display: 'block', marginBottom: 6 }}>
              ¡Candidatura aprobada!
            </strong>
            Felicidades, tu candidatura ha sido aprobada y está en fase de votación. Pronto anunciaremos quiénes son los finalistas y podrás saber si has resultado seleccionad@. Mantente atento a través de{' '}
            <a
              href="https://novagob.org/premios-novagob"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#fff', textDecoration: 'underline' }}
            >
              novagob.org/premios-novagob
            </a>
            . Si tienes alguna duda puedes contactarnos en{' '}
            <a href="mailto:info@novagob.org" style={{ color: '#fff', textDecoration: 'underline' }}>
              info@novagob.org
            </a>
            .
          </div>
        )}

        {showStatusMessage && nomination.status === 'rejected' && (
          <div
            style={{
              padding: '14px 16px',
              borderRadius: 8,
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.4)',
              color: '#fecaca',
              marginBottom: 20,
              fontSize: '0.95rem',
              lineHeight: 1.5,
            }}
          >
            <strong style={{ color: '#fca5a5', display: 'block', marginBottom: 6 }}>
              Candidatura rechazada
            </strong>
            Sentimos tener que rechazar tu candidatura, pero tras la revisión por parte del Comite de los Premios NovaGob no cumple correctamente con las bases. Puedes revisarlas en{' '}
            <a
              href="https://novagob.org/premios-novagob"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#fff', textDecoration: 'underline' }}
            >
              novagob.org/premios-novagob
            </a>
            . Si tienes alguna duda puedes contactarnos por correo en{' '}
            <a href="mailto:info@novagob.org" style={{ color: '#fff', textDecoration: 'underline' }}>
              info@novagob.org
            </a>
            .
          </div>
        )}

        {/* Categoría */}
        <div className="detail-section">
          <h3>Categoría</h3>
          <p>{category?.name}</p>
          {!publicView && (
            <span className={`badge badge-${nomination.status}`}>
              {nomination.status === 'pending' && 'Pendiente'}
              {nomination.status === 'approved' && 'Aprobada'}
              {nomination.status === 'rejected' && 'Rechazada'}
            </span>
          )}
        </div>

        {/* Campos para Benchmarking */}
        {isBenchmark && (
          <>
            <div className="detail-section">
              <h3>Contacto</h3>
              <p><strong>Nombre:</strong> {nomination.submitter_name}</p>
              {!publicView && (
                <p><strong>Email:</strong> {nomination.submitter_email}</p>
              )}
            </div>

            {nomination.benchmark_edicion_categoria && (
              <div className="detail-section">
                <h3>Edición/categoría en la que fue premiado</h3>
                <p>{nomination.benchmark_edicion_categoria}</p>
              </div>
            )}

            {nomination.benchmark_descripcion_breve && (
              <div className="detail-section">
                <h3>Descripción breve del proyecto</h3>
                <p>{nomination.benchmark_descripcion_breve}</p>
              </div>
            )}

            {nomination.benchmark_impacto_sostenido && (
              <div className="detail-section">
                <h3>Impacto sostenido acreditado</h3>
                <p>{nomination.benchmark_impacto_sostenido}</p>
              </div>
            )}

            {nomination.benchmark_elementos_replicables && (
              <div className="detail-section">
                <h3>Elementos que lo hacen replicable</h3>
                <p>{nomination.benchmark_elementos_replicables}</p>
              </div>
            )}

            {nomination.benchmark_administraciones_replicadoras && (
              <div className="detail-section">
                <h3>Administraciones que lo han replicado o tomado como referencia</h3>
                <p>{nomination.benchmark_administraciones_replicadoras}</p>
              </div>
            )}

            {nomination.benchmark_mejoras_aprendizajes && (
              <div className="detail-section">
                <h3>Mejoras o aprendizajes que ha inspirado</h3>
                <p>{nomination.benchmark_mejoras_aprendizajes}</p>
              </div>
            )}

            {nomination.benchmark_evidencias_enlaces && (
              <div className="detail-section">
                <h3>Evidencias y enlaces de verificación</h3>
                <p style={{ whiteSpace: 'pre-wrap' }}>
                  {nomination.benchmark_evidencias_enlaces}
                </p>
              </div>
            )}
          </>
        )}

        {/* Campos para categorías personales */}
        {isPersonal && (
          <>
            <div className="detail-section">
              <h3>Persona Candidata</h3>
              <p><strong>Nombre:</strong> {nomination.candidate_name}</p>
              {!publicView && (
                <p><strong>Email:</strong> {nomination.submitter_email}</p>
              )}
            </div>

            <div className="detail-section">
              <h3>Justificación</h3>
              <p>{nomination.candidate_reason}</p>
            </div>

            {nomination.candidate_cv_url && (
              <div className="detail-section">
                <h3>CV</h3>
                <a
                  href={nomination.candidate_cv_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 14px',
                    borderRadius: 8,
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid #ffc70055',
                    color: '#fff',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                  }}
                >
                  <span style={{ color: '#ffc700', display: 'inline-flex' }}>
                    <FileText size={16} />
                  </span>
                  <span>Ver CV</span>
                </a>
              </div>
            )}

            {nomination.candidate_social_links && (
              <div className="detail-section">
                <h3>Redes Sociales y Enlaces</h3>
                <SocialLinksList value={nomination.candidate_social_links} />
              </div>
            )}
          </>
        )}

        {/* Campos para proyectos estándar (excluye benchmark) */}
        {!isPersonal && !isBenchmark && (
          <>
            <div className="detail-section">
              <h3>Contacto</h3>
              <p><strong>Nombre:</strong> {nomination.submitter_name}</p>
              {!publicView && (
                <p><strong>Email:</strong> {nomination.submitter_email}</p>
              )}
            </div>

            <div className="detail-section">
              <h3>Título de la Iniciativa</h3>
              <p>{nomination.project_title}</p>
            </div>

            <div className="detail-section">
              <h3>Institución</h3>
              <p>{nomination.institution}</p>
            </div>

            <div className="detail-section">
              <h3>Resumen</h3>
              <p>{nomination.project_summary}</p>
            </div>

            <div className="detail-section">
              <h3>Público Objetivo</h3>
              <p>{nomination.target_audience}</p>
            </div>

            <div className="detail-section">
              <h3>Innovación Aportada</h3>
              <p>{nomination.innovation_description}</p>
            </div>

            <div className="detail-section">
              <h3>Colaboración</h3>
              <p>{nomination.collaboration_description}</p>
            </div>

            <div className="detail-section">
              <h3>Replicabilidad y Benchmarking</h3>
              <p>{nomination.replicability_description}</p>
            </div>

            <div className="detail-section">
              <h3>Impacto y Evaluación</h3>
              <p>{nomination.impact_description}</p>
            </div>

            <div className="detail-section">
              <h3>Equilibrio Coste/Beneficio</h3>
              <p>{nomination.cost_benefit_description}</p>
            </div>

            <div className="detail-section">
              <h3>Sostenibilidad</h3>
              <p>{nomination.sustainability_description}</p>
            </div>

            {parseProjectUrls(nomination.project_url).length > 0 && (
              <div className="detail-section">
                <h3>URL del Proyecto</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {parseProjectUrls(nomination.project_url).map((href, idx, arr) => {
                    const label = arr.length > 1 ? `URL ${idx + 1}` : 'Visitar';
                    return (
                      <a
                        key={idx}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '8px 14px',
                          borderRadius: 8,
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid #ffc70055',
                          color: '#fff',
                          textDecoration: 'none',
                          fontSize: '0.9rem',
                        }}
                      >
                        <span style={{ color: '#ffc700', display: 'inline-flex' }}>
                          <Globe size={16} />
                        </span>
                        <span>{label}</span>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {nomination.project_social_links && (
              <div className="detail-section">
                <h3>Redes Sociales del Proyecto</h3>
                <SocialLinksList value={nomination.project_social_links} />
              </div>
            )}
          </>
        )}

        {/* Información de revisión */}
        {!publicView && nomination.reviewed_at && (
          <div className="detail-section">
            <h3>Información de Revisión</h3>
            <p>
              <strong>Revisada el:</strong>{' '}
              {new Date(nomination.reviewed_at).toLocaleString()}
            </p>
          </div>
        )}

        {!publicView && nomination.last_modified_at && (
          <div className="detail-section">
            <p>
              <strong>Última modificación:</strong>{' '}
              {new Date(nomination.last_modified_at).toLocaleString()}
            </p>
          </div>
        )}
      </div>

      {/* En móvil, Aprobar/Rechazar quedan ocultos detrás de un botón
          "Decidir candidatura" para evitar pulsaciones accidentales al
          desplazarse por el contenido. En escritorio se muestran siempre
          (la CSS oculta el botón "Decidir" por encima de 768 px). */}
      <div
        className={`modal-footer${actionsRevealed ? ' is-revealed' : ''}`}
      >
        {showActions && (
          <button
            type="button"
            className="btn btn-secondary nomination-actions-reveal"
            onClick={() => setActionsRevealed(true)}
          >
            Decidir candidatura
          </button>
        )}

        {showActions && actionsRevealed && (
          <div className="nomination-actions-warn">
            ⚠️ Pulsando estos botones se enviará un correo a la persona
            postulante. Asegúrate antes de continuar.
          </div>
        )}

        {showActions && nomination.status !== 'approved' && (
          <button
            className="btn btn-success nomination-action-btn"
            onClick={onApprove}
          >
            {nomination.status === 'rejected' ? 'Cambiar a aprobada' : 'Aprobar'}
          </button>
        )}
        {showActions && nomination.status !== 'rejected' && (
          <button
            className="btn btn-danger nomination-action-btn"
            onClick={onReject}
          >
            {nomination.status === 'approved' ? 'Cambiar a rechazada' : 'Rechazar'}
          </button>
        )}
        {showActions && actionsRevealed && (
          <button
            type="button"
            className="btn btn-secondary nomination-actions-cancel"
            onClick={() => setActionsRevealed(false)}
          >
            Cancelar
          </button>
        )}
        {showClose && (
          <button className="btn btn-secondary" onClick={onClose}>
            Cerrar
          </button>
        )}
      </div>
    </div>
  );
};

export default NominationDetails;
